import { generateHmacSignature, getTimestamp } from './hmac';
import { getStoredTokens, saveTokens, clearTokens, isTokenExpired } from './tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const HMAC_SECRET = process.env.REACT_APP_HMAC_SECRET || '';

export interface ApiRequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  body?: any;
  requiresAuth?: boolean;
  requiresHmac?: boolean;
}

/**
 * Make a secure API request with HMAC signature and Cognito authentication
 */
export async function makeSecureRequest<T = any>(options: ApiRequestOptions): Promise<T> {
  const { method, path, body = null, requiresAuth = true, requiresHmac = true } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add authentication if required
  if (requiresAuth) {
    let { idToken, refreshToken } = getStoredTokens();

    if (!idToken) {
      throw new Error('Authentication required. Please log in.');
    }

    // Auto-refresh if the idToken is expired or about to expire
    if (isTokenExpired(idToken)) {
      if (!refreshToken) {
        throw new Error('Authentication required. Please log in.');
      }
      try {
        const refreshed = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!refreshed.ok) throw new Error('Token refresh failed');
        const data = await refreshed.json();
        saveTokens({
          idToken: data.idToken,
          refreshToken: refreshToken,
          expiresIn: data.expiresIn,
        });
        idToken = data.idToken;
      } catch {
        clearTokens();
        throw new Error('Session expired. Please log in again.');
      }
    }

    headers['Authorization'] = `Bearer ${idToken}`;
  }

  // Add HMAC signature if required
  if (requiresHmac) {
    const timestamp = getTimestamp();
    const signature = generateHmacSignature(method, path, body, timestamp, HMAC_SECRET);

    headers['X-HMAC-Signature'] = signature;
    headers['X-HMAC-Timestamp'] = timestamp;
  }

  // Make the request
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle response
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(
      errorData.message || `API request failed with status ${response.status}`,
    );
    // Keep the machine-readable half of the body. The QA governance surface
    // (Week12 / PC-07) returns `{ code, message, field }` from the
    // ERR-PC07E0N-NNN catalogue and its test cases assert on the code, not the
    // prose. Existing callers that only read `.message` are unaffected.
    Object.assign(error, { status: response.status, code: errorData.code, body: errorData });
    throw error;
  }

  return response.json();
}

/**
 * Make a GET request
 */
export function apiGet<T = any>(
  path: string,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'GET',
    path,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a POST request
 */
export function apiPost<T = any>(
  path: string,
  body: any,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'POST',
    path,
    body,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a PUT request
 */
export function apiPut<T = any>(
  path: string,
  body: any,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'PUT',
    path,
    body,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a DELETE request
 */
export function apiDelete<T = any>(
  path: string,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'DELETE',
    path,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Public API request (no auth, no HMAC)
 */
export function apiPublic<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: any,
): Promise<T> {
  return makeSecureRequest<T>({
    method,
    path,
    body,
    requiresAuth: false,
    requiresHmac: false,
  });
}
